import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Phone, Shield, CheckCircle, AlertTriangle, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";

const EmergencyResponseService = () => {
  const emergencySchema = {
    "@context": "https://schema.org",
    "@type": "EmergencyService",
    "name": "24-Hour Emergency Cyber Security Response",
    "provider": {
      "@type": "Organization",
      "name": "4FOX Security",
      "url": "https://4fox.uk",
      "telephone": "+44-XXXX-XXXXXX",
      "areaServed": {
        "@type": "Country",
        "name": "United Kingdom"
      }
    },
    "serviceType": "Emergency Cyber Security Incident Response",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://4fox.uk/services/24-hour-emergency-response",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+44-XXXX-XXXXXX",
        "contactType": "Emergency",
        "availableLanguage": "English",
        "hoursAvailable": "24/7"
      }
    },
    "offers": {
      "@type": "Offer",
      "description": "24/7 emergency cyber security incident response services for UK businesses"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is your emergency response time?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We guarantee a 15-minute callback for all emergency requests. Our UK-based security experts are available 24/7/365 to respond to cyber security incidents including ransomware attacks, data breaches, and system compromises."
        }
      },
      {
        "@type": "Question",
        "name": "What types of cyber security emergencies do you handle?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We respond to all types of cyber security emergencies including ransomware attacks, data breaches, malware infections, system compromises, DDoS attacks, insider threats, and suspected unauthorized access. Our team provides immediate containment, investigation, and recovery support."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide 24/7 emergency support across the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our emergency cyber security response team is available 24 hours a day, 7 days a week, 365 days a year. We serve businesses throughout the United Kingdom with immediate remote support and on-site response when required."
        }
      },
      {
        "@type": "Question",
        "name": "What should I do if I suspect a cyber security incident?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "If you suspect a cyber security incident: 1) Call our emergency hotline immediately, 2) Do not shut down affected systems unless advised, 3) Disconnect compromised systems from the network if safe to do so, 4) Preserve any evidence, 5) Do not pay ransoms without expert guidance. Our team will guide you through the initial response."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="24-Hour Emergency Cyber Security Response UK | Incident Response 24/7 | 4FOX Security"
        description="24/7 emergency cyber security incident response across the UK. 15-minute callback guarantee. Expert help for ransomware, data breaches, and security incidents. Call now for immediate assistance."
        canonical="https://4fox.uk/services/24-hour-emergency-response"
        keywords={[
          "24 hour cyber security UK",
          "emergency cyber security response",
          "cyber incident response 24/7",
          "ransomware response UK",
          "data breach emergency help",
          "cyber security crisis management",
          "24/7 security incident response",
          "emergency cyber attack help"
        ]}
        schema={{
          "@context": "https://schema.org",
          "@graph": [emergencySchema, faqSchema]
        }}
      />
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Services", href: "/services" },
              { label: "24-Hour Emergency Response" }
            ]}
          />

          {/* Emergency Alert Banner */}
          <div className="bg-destructive/10 border-l-4 border-destructive rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-destructive mb-2">
                  Experiencing a Cyber Security Emergency?
                </h2>
                <p className="text-foreground mb-4">
                  If you're currently experiencing a ransomware attack, data breach, or any other cyber security incident, 
                  call our emergency hotline now for immediate assistance.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" variant="destructive" className="gap-2">
                    <Phone className="w-5 h-5" />
                    Call Emergency Hotline
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/contact">Request Callback</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Section */}
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="destructive" className="gap-1">
                <Clock className="w-3 h-3" />
                24/7 Available
              </Badge>
              <Badge variant="outline">15-Minute Response</Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              24-Hour Emergency Cyber Security Response
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl">
              Expert emergency incident response for UK businesses. Our security specialists are available 
              24/7/365 to help you respond to ransomware attacks, data breaches, and critical security incidents.
            </p>
          </div>

          {/* Key Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card>
              <CardContent className="pt-6">
                <Clock className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-2">15-Minute Callback</h3>
                <p className="text-muted-foreground">
                  Guaranteed callback within 15 minutes of your emergency call. We understand that every minute counts during a cyber incident.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Shield className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-2">UK-Based Experts</h3>
                <p className="text-muted-foreground">
                  All our incident responders are based in the UK with extensive experience handling cyber security emergencies for British businesses.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Zap className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-2">Immediate Containment</h3>
                <p className="text-muted-foreground">
                  Rapid assessment and containment to minimize damage, protect critical data, and prevent further compromise of your systems.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* What We Handle */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Emergency Incidents We Handle</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Ransomware Attacks",
                  description: "Immediate response to ransomware encryption events. Containment, forensic analysis, ransom negotiation support, and recovery guidance."
                },
                {
                  title: "Data Breaches",
                  description: "Swift action to contain data breaches, assess exposure, meet ICO reporting requirements, and implement remediation measures."
                },
                {
                  title: "System Compromises",
                  description: "Investigation and remediation of compromised servers, workstations, or network infrastructure. Root cause analysis and security restoration."
                },
                {
                  title: "Malware Infections",
                  description: "Rapid detection, isolation, and removal of malware including trojans, worms, and advanced persistent threats (APTs)."
                },
                {
                  title: "DDoS Attacks",
                  description: "Immediate mitigation strategies for distributed denial of service attacks affecting your online services and business continuity."
                },
                {
                  title: "Insider Threats",
                  description: "Confidential investigation and response to suspected insider threats, unauthorized access, or malicious employee actions."
                }
              ].map((incident, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-bold mb-2">{incident.title}</h3>
                        <p className="text-muted-foreground">{incident.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Our Emergency Response Process */}
          <section className="mb-16 bg-muted/50 rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-8">Our Emergency Response Process</h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Immediate Triage (0-15 minutes)",
                  description: "Call received, initial assessment, expert assigned. We gather critical information and provide immediate guidance to contain the incident."
                },
                {
                  step: "2",
                  title: "Containment (15-60 minutes)",
                  description: "Isolate affected systems, prevent spread, preserve evidence. Our team works to stop the incident from escalating while maintaining business continuity where possible."
                },
                {
                  step: "3",
                  title: "Investigation (1-4 hours)",
                  description: "Forensic analysis to determine scope, entry point, and data impact. We identify what happened, how it happened, and what's been affected."
                },
                {
                  step: "4",
                  title: "Eradication (4-24 hours)",
                  description: "Remove threats, patch vulnerabilities, secure systems. Complete removal of the threat actor's access and tools from your environment."
                },
                {
                  step: "5",
                  title: "Recovery (24-72 hours)",
                  description: "Restore operations safely, verify system integrity. Careful restoration of systems with validation that the threat has been fully eliminated."
                },
                {
                  step: "6",
                  title: "Post-Incident Review",
                  description: "Detailed incident report, lessons learned, and recommendations to prevent recurrence. ICO notification support if required."
                }
              ].map((phase, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    {phase.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                    <p className="text-muted-foreground">{phase.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqSchema.mainEntity.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-bold mb-3">{faq.name}</h3>
                    <p className="text-muted-foreground">{faq.acceptedAnswer.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-primary text-primary-foreground rounded-lg p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Don't Face a Cyber Crisis Alone</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Our emergency response team is standing by 24/7 to help you navigate any cyber security incident. 
              Expert guidance when you need it most.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" variant="secondary" className="gap-2">
                <Phone className="w-5 h-5" />
                Call Emergency Line Now
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary" asChild>
                <Link to="/contact">Request Callback</Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EmergencyResponseService;
